/**
 * Drawing tab for shape creation tools.
 * Polygon, Rectangle, Circle, and Freeform shape drawing.
 */

import { useState } from 'react';
import { Undo2, Check, X, Send, Loader2, MapPinned } from 'lucide-react';
import type { AreaKind, DrawingTool } from '@vtak/shared';
import { useDrawingStore } from '@/stores/drawing-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { TOOL_LABELS, TOOL_ICONS } from '@/constants/drawing';
import { ShapeConfig } from './shape-config';
import { sendShapeToTak } from './send-shape';
import { saveMissionArea, canSaveAsArea } from './save-area';

/** Selectable area kinds when persisting a drawing as a mission area. */
const AREA_KIND_LABELS: ReadonlyMap<AreaKind, string> = new Map([
  ['area', 'Mission Area'],
  ['hazard-zone', 'Hazard Zone'],
  ['closure', 'Closure'],
]);

/** Drawing tools available in this tab */
const DRAW_TOOLS: DrawingTool[] = ['polygon', 'rectangle', 'circle', 'freeform'];

/** Drawing tab with tool selector and shape configuration */
export function DrawingTab(): React.JSX.Element {
  const { activeTool, drawingState, vertices, radiusMeters, startTool, removeLastVertex, finishDrawing, cancelDrawing, setSending, setSent, reset, shapeConfig } = useDrawingStore();
  const unitId = useWorkspaceStore((s) => s.workspace?.unitId ?? null);
  const [sendError, setSendError] = useState('');
  const [areaKind, setAreaKind] = useState<AreaKind>('area');
  const [areaSaved, setAreaSaved] = useState(false);

  const isDrawing = drawingState === 'drawing';
  const isConfirming = drawingState === 'confirming';
  const isSending = drawingState === 'sending';
  const isSent = drawingState === 'sent';
  const isDrawTool = activeTool !== null && DRAW_TOOLS.includes(activeTool);

  async function handleSend(): Promise<void> {
    if (!activeTool) return;
    setSending();
    setSendError('');
    const result = await sendShapeToTak(activeTool, vertices, radiusMeters, shapeConfig);
    setSent(result.ok);
    if (!result.ok) setSendError(result.error);
    if (result.ok) setTimeout(reset, 1500);
  }

  async function handleSaveArea(): Promise<void> {
    if (!activeTool || !unitId) return;
    setSendError('');
    const result = await saveMissionArea(unitId, activeTool, vertices, radiusMeters, shapeConfig, areaKind);
    if (!result.ok) {
      setSendError(result.error);
      return;
    }
    setAreaSaved(true);
    setTimeout(() => { setAreaSaved(false); reset(); }, 1500);
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      <div className="grid grid-cols-2 gap-2">
        {DRAW_TOOLS.map((tool) => {
          const Icon = TOOL_ICONS.get(tool)!;
          const isActive = activeTool === tool && isDrawing;
          return (
            <button
              key={tool}
              onClick={() => startTool(tool)}
              disabled={isConfirming || isSending}
              className={`flex items-center gap-2 rounded px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-echo-yellow text-black'
                  : 'bg-white/[0.06] text-foreground hover:bg-white/[0.1]'
              } disabled:opacity-50`}
            >
              <Icon size={16} />
              {TOOL_LABELS.get(tool)}
            </button>
          );
        })}
      </div>

      {isDrawing && isDrawTool && (
        <div className="space-y-2 rounded bg-white/[0.04] p-3">
          <p className="text-xs text-muted-foreground">
            {vertices.length} point{vertices.length !== 1 ? 's' : ''} placed.
            {activeTool === 'polygon' || activeTool === 'freeform'
              ? ' Double-click to finish.'
              : ''}
          </p>
          <div className="flex gap-2">
            <button
              onClick={removeLastVertex}
              disabled={vertices.length === 0}
              className="flex items-center gap-1 rounded bg-white/[0.06] px-3 py-1.5 text-xs text-foreground disabled:opacity-50"
            >
              <Undo2 size={12} /> Undo
            </button>
            {(activeTool === 'polygon' || activeTool === 'freeform') && vertices.length >= 3 && (
              <button
                onClick={finishDrawing}
                className="flex items-center gap-1 rounded bg-echo-yellow px-3 py-1.5 text-xs font-medium text-black"
              >
                <Check size={12} /> Done
              </button>
            )}
            <button
              onClick={cancelDrawing}
              className="flex items-center gap-1 rounded bg-white/[0.06] px-3 py-1.5 text-xs text-red-400"
            >
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      )}

      {isConfirming && isDrawTool && (
        <div className="space-y-3">
          <ShapeConfig />
          <div className="flex gap-2">
            <button
              onClick={handleSend}
              className="flex flex-1 items-center justify-center gap-1 rounded bg-echo-yellow px-3 py-2 text-xs font-medium text-black"
            >
              <Send size={12} /> Send to TAK
            </button>
            <button
              onClick={cancelDrawing}
              className="rounded bg-white/[0.06] px-3 py-2 text-xs text-red-400"
            >
              Cancel
            </button>
          </div>
          {unitId && canSaveAsArea(activeTool, vertices, radiusMeters) && (
            <div className="space-y-2 rounded bg-white/[0.04] p-3">
              <div className="flex flex-wrap gap-1">
                {[...AREA_KIND_LABELS.entries()].map(([kind, label]) => (
                  <button
                    key={kind}
                    onClick={() => setAreaKind(kind)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] ${
                      kind === areaKind
                        ? 'border-echo-yellow/50 bg-echo-yellow/15 text-echo-yellow'
                        : 'border-echo-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                onClick={handleSaveArea}
                disabled={areaSaved}
                className="flex w-full items-center justify-center gap-1 rounded bg-white/[0.08] px-3 py-2 text-xs font-medium text-foreground hover:bg-white/[0.12] disabled:opacity-50"
              >
                <MapPinned size={12} /> {areaSaved ? 'Saved to unit map' : 'Save as unit area'}
              </button>
            </div>
          )}
          {sendError && <p className="text-xs text-red-400">{sendError}</p>}
        </div>
      )}

      {isSending && <p className="text-xs text-muted-foreground"><Loader2 size={14} className="inline animate-spin" /> Sending...</p>}
      {isSent && <p className="text-xs text-green-400">Shape sent successfully.</p>}
    </div>
  );
}
